import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useT } from '../../helpers/i18n';
import { useRouter } from 'next/router';
import { Fancybox } from '@fancyapps/ui/dist/fancybox.esm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import Skeleton from 'react-loading-skeleton';

import * as appContext from '../../components/AppContext';
import BuilderHead from '../../components/head/BuilderHead';
import PropertyGalleryContent from '../../components/gallery/PropertyGalleryContent';
import Checkers from '../../helpers/class/Checkers';
import * as DataFetchHelper from '../../helpers/DataFetchHelper';
import * as MethodHelper from '../../helpers/MethodHelper';
import { setPageId } from '../../redux/actions';
import { wrapper } from '../../redux/store';
import { redis } from '../../redis';
import { getLangFromReq } from '../../utils/fromReq';
import { featureGroupsToFacilities } from '../../helpers/FacilitiesHelper';
import { getPropertyBedrooms, getPropertyBedroomById, getPropertySpaces, getPropertyPools, getPropertyMedia, getPropertyAlbums } from '../../api/client';

const waitForScrollToTop = ( maxWaitMs = 1500 ) => new Promise<void>( ( resolve ) => {
    if ( window.scrollY <= 0 ) {
        resolve();
        return;
    }
    const start = Date.now();
    const check = () => {
        if ( window.scrollY <= 0 || Date.now() - start > maxWaitMs ) {
            resolve();
            return;
        }
        requestAnimationFrame( check );
    };
    requestAnimationFrame( check );
} );

export default function Album ( props ) {
  const t = useT();
    const router = useRouter();

    const handleBack = () => {
        router.back();
    };

    const pageContext = useContext( appContext.page );
    pageContext.pageId = props.app_data.page_id;

    pageContext.isMobile = props.isMobile;
    pageContext.isTablet = props.isTablet;
    pageContext.isDesktop = props.isDesktop;

    pageContext.appSettings = props.app;

    const [ urlHref, setUrlHref ] = useState( null );
    React.useEffect( () => {
        if ( window !== undefined ) {
            setUrlHref( window.location.href );
        }
    }, [ urlHref ] );

    const domain = props.domain;
    const app = props.app;
    const head = props.head;
    const content = props.content;

    const chainProperties = Checkers.isValidArray( props.chainProperties ) ? props.chainProperties : null;

    const [ activePropertyId, setActivePropertyId ] = useState( content?.property_id ?? null );
    const [ activePropertyPhotos, setActivePropertyPhotos ] = useState(
        Checkers.isValidArray( content?.album ) ? content.album : []
    );
    const [ activeAlbums, setActiveAlbums ] = useState(
        ( Checkers.isValidArray( props.albums ) ? props.albums : [] ).filter( ( album ) => Checkers.isValidArray( album.photos ) )
    );
    const [ tabLoading, setTabLoading ] = useState( false );

    const lastSyncedContentPropertyId = React.useRef( content?.property_id ?? null );

    const contentIsStale = content?.property_id != null && content.property_id !== lastSyncedContentPropertyId.current;

    useEffect( () => {
        if ( content?.property_id == null || content.property_id === lastSyncedContentPropertyId.current ) return;

        lastSyncedContentPropertyId.current = content.property_id;
        setActivePropertyId( content.property_id );
        setActivePropertyPhotos( Checkers.isValidArray( content.album ) ? content.album : [] );
        setActiveAlbums(
            ( Checkers.isValidArray( props.albums ) ? props.albums : [] ).filter( ( album ) => Checkers.isValidArray( album.photos ) )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ content?.property_id ] );

    const handleTabClick = async ( propertyId ) => {
        if ( propertyId === activePropertyId ) return;

        if ( window.scrollY > 0 ) {
            window.scrollTo( { top: 0, behavior: 'smooth' } );
            await waitForScrollToTop();
        }

        setActivePropertyId( propertyId );
        setTabLoading( true );

        const selectedProperty = chainProperties?.find( ( p ) => p.id === propertyId );
        const propertySlug = Checkers.isValidString( selectedProperty?.sourcePath )
            ? selectedProperty.sourcePath.replace( /^\/+/, '' )
            : propertyId;
        const nextQuery = { ...router.query };
        delete nextQuery.property_id;
        router.replace(
            { pathname: router.pathname, query: { ...nextQuery, property: propertySlug } },
            undefined,
            { shallow: true }
        );

        try {
            const [ mediaRes, albumsRes ]: any = await Promise.all( [
                getPropertyMedia( props.app_data.page_id, propertyId ),
                getPropertyAlbums( props.app_data.page_id, propertyId ),
            ] );
            setActivePropertyPhotos( Checkers.isValidArray( mediaRes?.images ) ? mediaRes.images : [] );
            setActiveAlbums(
                ( Checkers.isValidArray( albumsRes?.albums ) ? albumsRes.albums : [] ).filter( ( album ) => Checkers.isValidArray( album.photos ) )
            );
        } catch ( e ) {
            setActivePropertyPhotos( [] );
            setActiveAlbums( [] );
        }
        setTabLoading( false );
    };

    const propertyPhotos = activePropertyPhotos;
    const albums = activeAlbums;

    const [ bedroomAlbums, setBedroomAlbums ] = useState<any[]>( [] );
    const [ spaceAlbums, setSpaceAlbums ] = useState<any[]>( [] );
    const [ poolAlbums, setPoolAlbums ] = useState<any[]>( [] );
    const [ galleryLoading, setGalleryLoading ] = useState<boolean>( activePropertyId != null );
    useEffect( () => {
        const propertyId = activePropertyId;
        if ( propertyId == null ) {
            setGalleryLoading( false );
            return;
        }
        setGalleryLoading( true );
        let cancelled = false;

        const bedroomsPromise = getPropertyBedrooms( props.app_data.page_id, propertyId )
            .then( async ( res: any ) => {
                const bedrooms = ( Checkers.isValidArray( res?.bedrooms ) ? res.bedrooms : [] )
                    .filter( ( b ) => /^\d+$/.test( String( b.bedroom_id ) ) );
                if ( cancelled || bedrooms.length === 0 ) return;

                const details = await Promise.all( bedrooms.map( async ( b ) => {
                    try {
                        const detailRes: any = await getPropertyBedroomById( props.app_data.page_id, propertyId, b.bedroom_id );
                        return Checkers.isValidArray( detailRes?.bedrooms ) ? detailRes.bedrooms[0] : null;
                    } catch ( e ) {
                        return null;
                    }
                } ) );
                if ( cancelled ) return;

                const nextBedroomAlbums = bedrooms
                    .map( ( b, index ) => {
                        const detail: any = details[index];
                        const photos = detail != null && Checkers.isValidArray( detail.media ) ? detail.media : [];
                        if ( photos.length === 0 ) return null;
                        return {
                            id: 'bedroom-' + b.bedroom_id,
                            title: Checkers.isValidString( b.bedroom_title ) ? b.bedroom_title : t('bedroom.numbered', { n: index + 1 }),
                            photos: photos,
                            sort: parseFloat( b.bedroom_sort ),
                            facilities: featureGroupsToFacilities( detail.features ),
                        };
                    } )
                    .filter( ( a ) => a != null );
                setBedroomAlbums( nextBedroomAlbums );
            } )
            .catch( () => undefined );

        const spacesPromise = getPropertySpaces( props.app_data.page_id, propertyId )
            .then( async ( res: any ) => {
                const spaces = Checkers.isValidArray( res?.spaces ) ? res.spaces : [];
                if ( cancelled || spaces.length === 0 ) return;

                const details = await Promise.all( spaces.map( async ( s ) => {
                    try {
                        const detailRes: any = await getPropertySpaces( props.app_data.page_id, propertyId, 'space_id=' + s.space_id );
                        return Checkers.isValidArray( detailRes?.spaces ) ? detailRes.spaces[0] : null;
                    } catch ( e ) {
                        return null;
                    }
                } ) );
                if ( cancelled ) return;

                const nextSpaceAlbums = spaces
                    .map( ( s, index ) => {
                        const detail: any = details[index];
                        const photos = detail != null && Checkers.isValidArray( detail.media ) ? detail.media : [];
                        if ( photos.length === 0 ) return null;
                        return {
                            id: 'space-' + s.space_id,
                            title: Checkers.isValidString( s.space_title ) ? s.space_title : t('gallery.spaceNumbered', { n: index + 1 }),
                            photos: photos,
                            sort: parseFloat( s.space_sort ),
                            facilities: featureGroupsToFacilities( detail.features ),
                        };
                    } )
                    .filter( ( a ) => a != null );
                setSpaceAlbums( nextSpaceAlbums );
            } )
            .catch( () => undefined );

        const poolsPromise = getPropertyPools( props.app_data.page_id, propertyId )
            .then( ( res: any ) => {
                if ( cancelled ) return;
                const pools = Checkers.isValidArray( res?.pools ) ? res.pools : [];
                setPoolAlbums(
                    pools
                        .filter( ( p ) => Checkers.isValidArray( p.media ) )
                        .map( ( p, index ) => ( {
                            id: 'pool-' + p.pool_id,
                            title: Checkers.isValidString( p.pool_title ) ? p.pool_title : t('gallery.poolNumbered', { n: index + 1 }),
                            photos: p.media,
                            sort: parseFloat( p.pool_sort ),
                            facilities: featureGroupsToFacilities( p.features ),
                        } ) )
                );
            } )
            .catch( () => undefined );

        Promise.all( [ bedroomsPromise, spacesPromise, poolsPromise ] ).finally( () => {
            if ( !cancelled ) setGalleryLoading( false );
        } );

        return () => { cancelled = true; };
    }, [ activePropertyId, props.app_data.page_id ] );

    const bySort = ( list ) => [ ...list ].sort( ( a, b ) => {
        const sa = Number.isFinite( a.sort ) ? a.sort : Infinity;
        const sb = Number.isFinite( b.sort ) ? b.sort : Infinity;
        return sa === sb ? 0 : sa - sb;
    } );

    const allAlbumsForDisplay = useMemo( () => {
        const isCommonArea = ( album ) => /shared|common/i.test( album.title );
        return [
            ...albums,
            ...bySort( spaceAlbums ),
            ...bySort( bedroomAlbums.filter( ( a ) => !isCommonArea( a ) ) ),
            ...bySort( bedroomAlbums.filter( isCommonArea ) ),
            ...bySort( poolAlbums ),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ JSON.stringify( albums.map( ( a ) => a.id ) ), spaceAlbums, bedroomAlbums, poolAlbums ] );

    const allAlbums = useMemo(
        () => propertyPhotos.length > 0
            ? [ { id: 'main gallery', title: t('gallery.main'), photos: propertyPhotos }, ...allAlbumsForDisplay ]
            : allAlbumsForDisplay,
        [ propertyPhotos, allAlbumsForDisplay ]
    );

    const image_fb_meta = Checkers.isValidArray( head.og_image ) ? head.og_image[0].image : null;

    useEffect( () => {
        Fancybox.bind( '[data-fancybox^="album-"]', { Hash: false } );
        return () => {
            Fancybox.unbind( '[data-fancybox^="album-"]' );
            Fancybox.close();
        };
    }, [ allAlbums ] );

    const tabsData = albums.map( ( album ) => {
        return {
            tabId: 'album' + album.id,
            tabTitle: album.title,
            tabContent: (
                <div className="mzn-grid">
                    {
                        Checkers.isValidArray( album.photos ) && album.photos.map( ( photo, index ) => {
                            const caption = Checkers.isValidString( photo.caption ) ? photo.caption.trim() : '';
                            const src = photo.image_thumb != undefined ? photo.image_thumb : photo.image;
                            return (
                                <div
                                    key={photo.id != null ? photo.id.toString() : src}
                                    className="mzn-card"
                                    data-fancybox={'album-' + album.id}
                                    data-src={photo.image}
                                    data-thumb={src}
                                >
                                    <div className="mzn-imgwrap">
                                        <img src={src} alt={caption || t('gallery.imageNumbered', { n: index + 1 })} loading="lazy" />
                                    </div>
                                    <div className="mzn-caption fs-12">{caption.length > 0 ? caption : t('gallery.photoNumbered', { n: index + 1 })}</div>
                                </div>
                            );
                        } )
                    }
                </div>
            )
        };
    } );

    return (
        <>
            <BuilderHead
                title={( head.data.title + ' - ' + head.data.motto )}
                keywords={head.data.meta_keywords}
                titleOfficial={head.data.titleOfficial}
                domain={urlHref}
                icon_60={head.data.icon_60}
                icon_76={head.data.icon_76}
                icon_120={head.data.icon_120}
                icon_152={head.data.icon_152}
                icon_256={head.data.icon_256}
                icon={head.data.icon}
                title_fb_meta={( head.data.title + ' - ' + head.data.motto )}
                desc_fb_meta={head.data.meta_description}
                image_fb_meta={image_fb_meta}
                site_name_fb_meta={head.data.title}
                lat_fb_meta={head.location.latitude}
                lon_fb_meta={head.location.longitude}
                twitter_img_alt={head.data.title + ' - ' + head.data.motto + '. ' + head.data.meta_description}
                googleSchemaContext={domain}
                googleSchemaTitle={head.data.title}
                googleSchemaStreetAddress={head.location.address}
                googleSchemaCity={head.location.city}
                googleSchemaCountry={head.location.country}
                googleSchemaZip={head.location.zip}
                googleSchemaPhone={head.location.phone}
                designColor={app.designColor}
                font={app.font}
                manager={false}
            />

            <div className="p-3" style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 1 }}>
                <div style={{ position: 'relative' }}>
                    <button type="button" className="btn btn-link text-dark" onClick={handleBack} aria-label={t('a11y.back')}
                            style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}>
                        <FontAwesomeIcon icon={faArrowLeft} className="fs-20" />
                    </button>
                    <h1 className="h5 text-center m-0">{t('gallery.title')}</h1>
                </div>
                {
                    chainProperties != null && chainProperties.length > 1 &&
                        <ul className="nav nav-tabs w-100 nav-tabs-simple-bold album-tabs" role="tablist">
                            {
                                chainProperties.map( ( property ) => (
                                    <li key={property.id}>
                                        <a
                                            className={'fs-12' + ( property.id === activePropertyId ? ' active' : '' ) + ( tabLoading ? ' disabled' : '' )}
                                            href={'#'}
                                            onClick={( e ) => { e.preventDefault(); handleTabClick( property.id ); }}
                                        >
                                            <span>{property.title}</span>
                                        </a>
                                    </li>
                                ) )
                            }
                        </ul>
                }
                <style jsx>{`
                    .album-tabs:after {
                        display: none;
                    }
                    @media (max-width: 767px) {
                        .album-tabs {
                            display: flex;
                            flex-wrap: nowrap;
                            overflow-x: auto;
                            -webkit-overflow-scrolling: touch;
                            justify-content: flex-start;
                            padding-bottom: 12px;
                        }
                        .album-tabs > li {
                            flex: 0 0 auto;
                        }
                    }
                    @media (min-width: 768px) {
                        .album-tabs {
                            justify-content: center;
                        }
                    }
                `}</style>
            </div>

            {
                ( galleryLoading || contentIsStale ) && !tabLoading ? (
                    <div className="container">
                        <section className="p-b-20 p-t-40">
                            <div className="masonry">
                                <div className="mzn-grid mzn-grid-covers">
                                    {
                                        Array.from( { length: 8 } ).map( ( _, i ) => (
                                            <div className="mzn-card" key={i}>
                                                <div className="mzn-imgwrap">
                                                    <Skeleton height="100%" containerClassName="d-block h-100" />
                                                </div>
                                                <div className="mzn-caption fs-12"><Skeleton width={70} /></div>
                                            </div>
                                        ) )
                                    }
                                </div>
                            </div>
                        </section>
                        <section className="p-t-30 p-b-30">
                            <div className="masonry">
                                <div className="row">
                                    <div className="col-md-4 mzn-gallery-info mb-4">
                                        <h2 className="mb-0"><Skeleton width={160} /></h2>
                                    </div>
                                    <div className="col-md-8 mzn-gallery-photos">
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
                                            {
                                                Array.from( { length: 6 } ).map( ( _, i ) => (
                                                    <Skeleton key={i} height={220} containerClassName="d-block" />
                                                ) )
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    </div>
                ) : (
                    <PropertyGalleryContent albums={allAlbumsForDisplay} propertyPhotos={propertyPhotos} />
                )
            }
        </>
    );
}

export const getServerSideProps = wrapper.getServerSideProps( ( store ) => async (
    {
        req,
        res,
        query
    } ) => {

    const [ isMobile, isTablet, isDesktop ] = Checkers.getUserAgentData( store, req );

    res.setHeader(
        'Cache-Control',
        'public, s-maxage=10, stale-while-revalidate=59'
    );

    let subdomain = null;
    const slug_data = null;

    let head = null;
    let app = null;
    let page = null;
    let app_data = null;
    let navigation = null;
    let content = null;
    let html_sections = null;
    let script_sections = null;
    let locations = null;
    let footer = null;
    let rgbSearchbarColor = null;
    let rgbDesignColor = null;
    let contactHtml = null;
    let albums = [];

    let manifest = null;

    const domain = req.headers.host;
    const hostname_parts = req.headers.host.split( '.' );
    const parts = hostname_parts.length;

    app_data = await DataFetchHelper.fetchAppData( req, redis, res );
    if ( app_data == false ) {
        return {
            notFound: true,
        };
    }

    store.dispatch( setPageId( app_data.page_id ) );

    await DataFetchHelper.fetchSystemData( redis, app_data.page_id, store ).then( ( response ) => {
        head = response.head;
        app = response.app;
        page = response.page;
        navigation = response.navigation;
        locations = response.locations;
        footer = response.footer;
        html_sections = response.html_sections;
        contactHtml = response.contactHtml;
    } );

    content = await DataFetchHelper.fetchContentData( redis, app_data.page_id );

    if ( content == null ) {
        return {
            notFound: true,
        };
    }

    let chainProperties = null;

    // On a chain/PM site, page-level content has no single property_id of its
    // own - show a tab strip of the chain's own properties instead, with the
    // requested (or first) one's gallery selected by default.
    if ( content.is_chain == true ) {
        const properties_params = Checkers.getPropertiesParams( store );
        const propertiesRes = await DataFetchHelper.fetchData(
            redis, "/api/builder/properties/manager/list/data/v2", "properties",
            app_data.page_id, { ...properties_params, limit: 20 }, 1, null, true
        );
        const propertiesList = Checkers.isValidArray( propertiesRes?.data ) ? propertiesRes.data
            : Checkers.isValidArray( propertiesRes?.properties ) ? propertiesRes.properties
                : Checkers.isValidArray( propertiesRes ) ? propertiesRes
                    : [];

        chainProperties = propertiesList.map( ( property ) => ( {
            id: property.property_id,
            title: property.property_title,
            sourcePath: property.sourcePath,
        } ) );

        // // TEMPORARY - visual testing only, remove once you've checked the
        // // tab strip with many properties on desktop/mobile.
        // if ( query.testManyTabs === '1' && chainProperties.length > 0 ) {
        //     const base = chainProperties[0];
        //     const extras = Array.from( { length: 6 } ).map( ( _, i ) => ( {
        //         ...base,
        //         id: base.id + '-test-' + i,
        //         title: base.title + ' ' + ( i + 2 ),
        //     } ) );
        //     chainProperties = [ ...chainProperties, ...extras ];
        // }

        const requestedPropertySlug = query.property;
        const requestedPropertyId = query.property_id;
        const selectedProperty = requestedPropertySlug
            ? chainProperties.find( ( p ) => Checkers.isValidString( p.sourcePath ) && p.sourcePath.replace( /^\/+/, '' ) === requestedPropertySlug )
            : requestedPropertyId
                ? chainProperties.find( ( p ) => String( p.id ) === String( requestedPropertyId ) )
                : chainProperties[0];

        if ( selectedProperty == null ) {
            return {
                notFound: true,
            };
        }
        
        if ( !requestedPropertySlug && Checkers.isValidString( selectedProperty.sourcePath ) ) {
            const params = new URLSearchParams( query as Record<string, string> );
            params.delete( 'property_id' );
            params.set( 'property', selectedProperty.sourcePath.replace( /^\/+/, '' ) );
            return {
                redirect: {
                    destination: '/album?' + params.toString(),
                    permanent: false,
                },
            };
        }

        content = { ...content, property_id: selectedProperty.id, album: [] };
    }

    if ( !content.property_id ) {
        return {
            notFound: true,
        };
    }

    // main gallery always shows the property's own photos; page photos are only the fallback
    const selectedMedia = await DataFetchHelper.fetchSelectedPropertyMedia( redis, app_data.page_id, content.property_id );
    if ( Checkers.isValidArray( selectedMedia?.images ) ) {
        content.album = selectedMedia.images;
    }

    const propertyAlbums = await DataFetchHelper.fetchPropertyAlbums( redis, app_data.page_id, content.property_id );
    if ( Checkers.isValidArray( propertyAlbums?.albums ) ) {
        albums = propertyAlbums.albums;
    }

    manifest = MethodHelper.initManifest( head, app, '/?source=pwa' );
    manifest = MethodHelper.extendManifest( manifest, head );

    const json = JSON.stringify( manifest );
    const fs = require( 'fs' );
    fs.writeFile( './public/manifest.json', json, ( err ) => {
        if ( err ) {
            // eslint-disable-next-line no-console
            console.error( err );
            return;
        }
    } );

    if ( app.searchbarBgColor != null )
        rgbSearchbarColor = MethodHelper.hexToRgb( app.searchbarBgColor );
    if ( app.designColor )
        rgbDesignColor = MethodHelper.hexToRgb( app.designColor );

    return {
        props: {
            app_data: app_data,
            head: head,
            page: page,
            builder: app != null && app.builder != null ? app.builder : null,
            app: app,
            navigation: navigation,
            content: content,
            html_sections: html_sections,
            script_sections: script_sections,
            locations: locations,
            footer: footer,
            domain: domain,
            subdomain: subdomain,
            parts: parts,
            slug_data: slug_data,
            rgbDesignColor: rgbDesignColor,
            rgbSearchbarColor: rgbSearchbarColor,
            isDesktop: isDesktop,
            contactHtml: contactHtml,
            albums: albums,
            chainProperties: chainProperties
        }
    };
} );
